import React, { useEffect, useRef, useState } from "react";
import { Card, Button, Typography, Space, Spin, Grid, Tooltip, Switch } from "antd";
import { ArrowLeftOutlined, FilePdfOutlined, InfoCircleOutlined, PrinterOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import SearchListAutoComplete from "../../../../components/SearchListAutoComplete";
import ReportDom from "../../../../components/common/ReportDom";
import { getInstitutionApi } from "../../../../apiservice/setting";
import { serviceExpenceVoucherApi } from "../../../../apiservice/service";

import ServiceExpenseVoucherBody from "./components/ServiceExpenseVoucherBody";
import ServiceExpenseVoucherBodyDesignA from "./components/ServiceExpenseVoucherBodyDesignA";
import { useSelector } from "react-redux";
import { selectAuthInfo } from "../../../Auth/authSelectors";
import Swal from "sweetalert2";
import "./components/voucher-mobile.css"
import { downloadPdf } from "../../../../lib/functions";
import { useShareDocument } from "../../../../hooks/useShareDocument";
import ShareDocumentButton from "../../../../components/ShareButton/ShareDocumentButton";

const { Text } = Typography;

const ServiceExpenseVoucher = () => {
  const navigate = useNavigate();
  const reportRef = useRef(null);
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const authInfo = useSelector(selectAuthInfo);

  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [vouchersList, setVouchersList] = useState([]);
  const [institution, institutionSet] = useState(null);
  const [showStamp, setShowStamp] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [showShipping, setShowShipping] = useState(false);
  const [voucherNo, setVoucherNo] = useState(null);
  const [accName, setAccName] = useState(null);
  const [customerEmail, setCustomerEmail] = useState("");

  const {
    shareOpen,
    setShareOpen,
    email,
    setEmail,
    // loading,
    handleEmailShare,
    handleWhatsAppShare,
  } = useShareDocument();

 

  useEffect(() => {
    const fetchVouchers = async () => {
      setLoading(true);
      try {
        const res = await serviceExpenceVoucherApi({}); // fetch all vouchers
        setVouchersList(res.data || []);
      } catch {
        setVouchersList([]);
      } finally {
        setLoading(false);
      }
    };

    let getInstitution = async () => {
    await getInstitutionApi()
      .then((res) => {
        
        institutionSet(res.data);
      })
      .catch((err) => {
        if (err.response?.data?.message) {
	
          Swal({ 
              title: err.response.data.message, 
              icon: "warning" 
          });
      } else {
          Swal({ 
              title: "Internal Error",  text: "If the issue persists, please contact our support team.", 
              icon: "warning" 
          });
      }
      });
  };
    getInstitution();
    fetchVouchers();
  }, []);

   const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: `Service Expense Voucher-${voucherNo ? voucherNo : ""}`,
    removeAfterPrint: true,
  });

  useEffect(() => {
    if (shareOpen && customerEmail && !email) {
      setEmail(customerEmail);
    }
  }, [shareOpen, customerEmail, email, setEmail]);

  return (
    <div className="p-4">
      {/* Header Bar */}
      {/* Header Bar */}
        <div
        style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 16,
            gap: 8,
        }}
        >
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
            {!screens.xs && "Back"}
        </Button>

        <div style={{ flex: 1, maxWidth: screens.xs ? 200 : 400 }}>
            <SearchListAutoComplete
            entityType="voucher"
            placeholder="Voucher No"
            optionsList={(vouchersList || []).map((v) => ({
                pur_id: v.pur_r_id,
                display_text: v.display_text,
                raw: v,
            }))}
            style={{alignItems:"center"}}
            onSelect={(obj) => setSelectedVoucher(obj?.raw || null)}
            showAddNew={false}
            />
        </div>

        <Space align="center">
          <Switch
            checked={showShipping}
            onChange={setShowShipping}
            checkedChildren="Shipping"
            unCheckedChildren="Shipping"
          />

          <Switch
            checked={showStamp}
            onChange={setShowStamp}
            checkedChildren="Stamp"
            unCheckedChildren="Stamp"
          />

          <Switch
            checked={showSignature}
            onChange={setShowSignature}
            checkedChildren="Signature"
            unCheckedChildren="Signature"
          />

          <ShareDocumentButton
          disabled={!selectedVoucher}
          screens={screens}
          shareOpen={shareOpen}
          setShareOpen={setShareOpen}
          email={email}
          setEmail={setEmail}
          loading={loading}
          onEmail={() =>
            handleEmailShare({
              url: `/print/service-expense/${selectedVoucher.service_ex_id}`,
              fileName: `ServiceExpense_${voucherNo || selectedVoucher.display_text}.pdf`,
              name: accName || "",
              docNo: voucherNo || selectedVoucher.display_text,
            })
          }
          onWhatsApp={() =>
            handleWhatsAppShare({
              url: `/print/service-expense/${selectedVoucher.service_ex_id}`,
              fileName: `ServiceExpense_${voucherNo || selectedVoucher.display_text}.pdf`,
              text: `Hello ${accName || ""}, Your Invoice ${voucherNo || selectedVoucher.display_text} is ready. Download here:`,
            })
          }
        />
        <Button
          type="primary"
          icon={<FilePdfOutlined />}
          disabled={!selectedVoucher || !voucherNo || pdfLoading }
          loading={pdfLoading}
          onClick={async () =>{
            try {
            setPdfLoading(true);
            await downloadPdf(
              `/print/service-expense/${selectedVoucher.service_ex_id}`,
              `ServiceExpense_${voucherNo || selectedVoucher.display_text}.pdf`
            );
            } catch (err) {
              console.error(err);
            } finally {
              setPdfLoading(false);
            }
          }}
        >
          {!screens.xs && "Download"}
        </Button>

        <Button
            type="primary"
            icon={<PrinterOutlined />}
            onClick={handlePrint}
            disabled={!selectedVoucher}
        >
            { !screens.xs && "Print" }
        </Button>
        <Tooltip
            placement="bottom"
            title={
              <div style={{ maxWidth: 260 }}>
                <strong>Print Tip</strong>
                <div style={{ marginTop: 6 }}>
                  For best results, please disable
                  <b> “Headers and Footers”</b> in your browser’s print settings.
                </div>
              </div>
            }
          >
            <InfoCircleOutlined
              style={{
                color: "#888",
                fontSize: 16,
                cursor: "pointer",
              }}
            />
          </Tooltip>
        </Space>  
        </div>


      {/* Voucher Display */}
      <Card className="shadow-md rounded-xl voucher-wrapper">
        {!selectedVoucher ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            {loading ? (
              <Spin />
            ) : (
              <Text type="secondary">Search and select a voucher</Text>
            )}
          </div>
        ) : (<ServiceExpenseVoucherBody service_ex_id={selectedVoucher.service_ex_id} institution={institution} showStamp={showStamp} showSignature={showSignature} showShipping={showShipping} setVoucherNo={setVoucherNo} setAccName={setAccName} setCustomerEmail={setCustomerEmail}/>
        )}
      </Card>

      {/* Hidden print layout */}
      {selectedVoucher && (
        <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
          <ReportDom
            ref={reportRef}
            mode={institution?.print_template === "design_a" ? "no-title" : (showSignature ? "signatureOn" : showStamp ? "signatureOn" : "")}
            // title="Service Expense Voucher"
            renderTable={() => (
              institution?.print_template === "design_a" ? (
                <ServiceExpenseVoucherBodyDesignA service_ex_id={selectedVoucher.service_ex_id} showStamp={showStamp} showSignature={showSignature} showShipping={showShipping} />
              ) : (
                <ServiceExpenseVoucherBody service_ex_id={selectedVoucher.service_ex_id} institution={institution} printMode showStamp={showStamp} showSignature={showSignature} showShipping={showShipping} />
              )
            )}
          />
        </div>
      )}
    </div>
  );
};

export default ServiceExpenseVoucher;
